import { HEROES, ISLA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { FloesRules, IslaRules, LagoonRules, SealRules } from '../isla-types.ts';
import type { Hex } from '../hex.ts';
import { FLOES_RADIUS, type FloesLayout, type LagoonLayout, type SealLayout } from './isla-layout.ts';
import { floesGoal, floesHold, floesResult, floesTick } from './floes.ts';
import { lagoonGoal, lagoonOff, lagoonOrder, lagoonResult, lagoonTick } from './lagoon.ts';
import { sealGoal, sealHold, sealResult, sealTick } from './seal.ts';

/**
 * The Long Hunt's chapter 4 (docs/sendas/barbaro.md, «La Isla Blanca»): its rules from their layouts (the fight's builder
 * and the renderer's scenes build them the same way; lives in hit points worked out by the caller, seconds of D) and the
 * one door battle/senda.ts goes through for its three acts (`rules.senda.isla`).
 */

const ids = (R: number, hs: Hex[]): number[] =>
  hs
    .map((h) => cellId(R + 2, h.q, h.r))
    .filter((c) => c >= 0)
    .sort((a, b) => a - b);

/** The floes' whole cycle (joined, cracking, parted), the layout's offsets are read in. */
export const FLOES_CYCLE = ISLA.floes.join + ISLA.floes.warn + ISLA.floes.apart;

export const floesRules = (L: FloesLayout, wolfHp: number, colossusHp: number): FloesRules => {
  const F = ISLA.floes;
  const R = FLOES_RADIUS;
  return {
    shore: ids(R, L.shore),
    camp: { ...L.camp },
    campCells: ids(R, L.campCells),
    floes: L.floes.map((p) => ({ ...p })),
    floeCells: L.floeCells.map((c) => ids(R, c)),
    spawns: L.spawns.map((p) => ({ ...p })),
    necks: L.necks.map((c) => ids(R, c)),
    neckAt: L.neckAt.map((p) => ({ ...p })),
    landing: L.landing.map((p) => ({ ...p })),
    offsets: L.offsets.slice(),
    join: F.join,
    warn: F.warn,
    apart: F.apart,
    crack: { first: F.crack.first, every: [F.crack.every[0], F.crack.every[1]], len: F.crack.len, open: F.crack.open },
    fallBp: F.fallBp,
    bands: F.bands.map((b) => [b[0], b[1]] as [number, number]),
    first: F.first,
    every: F.every,
    gap: F.gap,
    parts: F.parts,
    part: F.part,
    wolfHp,
    colossusHp,
    colossusHit: F.colossusHit,
    colossusDmgBp: F.colossusDmgBp,
    campReach: F.campReach,
    gnaw: F.gnaw,
    gnawReach: F.gnawReach,
  };
};

export const lagoonRules = (L: LagoonLayout, wolfHp: number): LagoonRules => {
  const G = ISLA.lagoon;
  return {
    tracks: L.tracks.map((t) => ({ ...t, pts: t.pts.map((p) => ({ ...p })) })),
    crossings: L.crossings.map((c) => ({ ...c, at: { ...c.at } })),
    ends: L.ends.map((p) => ({ ...p })),
    last: L.last,
    see: G.see,
    readT: G.readT,
    reach: G.reach,
    breakT: G.breakT,
    fallBp: G.fallBp,
    sinkR: G.sinkR,
    lostR: G.lostR,
    sinkWolves: G.sinkWolves,
    shore: L.shore.map((p) => ({ ...p })),
    wolves: { first: G.wolves.first, every: G.wolves.every, n: [G.wolves.n[0], G.wolves.n[1]] },
    wolfHp,
  };
};

export const sealRules = (L: SealLayout, wolfHp: number, sealHp: number, howlerHp: number): SealRules => {
  const S = ISLA.seal;
  return {
    fire: { ...L.fire },
    holes: L.holes.map((p) => ({ ...p })),
    rim: L.rim.map((p) => ({ ...p })),
    start: { ...L.sealAt },
    hp: sealHp,
    biteBp: S.biteBp,
    reload: S.reload,
    reach: S.reach,
    speed: S.speed,
    aggro: S.aggro,
    slide: { ...S.slide, every: [S.slide.every[0], S.slide.every[1]] },
    fleeBp: S.fleeBp,
    look: S.look,
    block: S.block,
    caught: S.caught,
    stun: S.stun,
    under: S.under,
    healBp: S.healBp,
    raise: S.raise,
    howlers: L.howlers.map((p) => ({ ...p })),
    howler: { hp: howlerHp, first: S.howler.first, every: S.howler.every, rear: S.howler.rear, howl: S.howler.howl, cutBp: S.howler.cutBp, raise: S.howler.raise, stun: S.howler.stun },
    pack: { first: S.pack.first, every: [S.pack.every[0], S.pack.every[1]], n: [S.pack.n[0], S.pack.n[1]] },
    fireReach: S.fireReach,
    gnaw: S.gnaw,
    gnawReach: S.gnawReach,
    wolfHp,
  };
};

/** (The hero's pace, for the bots and the drawing: the Seal's flight is a little under it.) */
export const ISLA_HERO_PACE = HEROES.barbarian.speed;

const ir = (s: BattleState): IslaRules | undefined => s.rules?.senda?.isla;

export const islaTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = ir(s);
  if (r?.floes) floesTick(s, cue);
  else if (r?.lagoon) lagoonTick(s, cue);
  else if (r?.seal) sealTick(s, cue);
};

/** The hero's tap on one of the act's marks (the lagoon's ends). */
export const islaOrder = (s: BattleState, h: Hero, at: number): void => {
  if (ir(s)?.lagoon) lagoonOrder(s, h, at);
};

/** Another order of the hero's own: the break it was sent to is off. */
export const islaMarkOff = (s: BattleState): void => {
  if (ir(s)?.lagoon) lagoonOff(s);
};

export const islaGoal = (s: BattleState): { done: number; need: number } => {
  const r = ir(s);
  return r?.floes ? floesGoal(s) : r?.lagoon ? lagoonGoal(s) : sealGoal(s);
};

export const islaResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = ir(s);
  return r?.floes ? floesResult(s) : r?.lagoon ? lagoonResult(s) : r?.seal ? sealResult(s) : null;
};

/** The act moved this nightmare itself (true). */
export const islaHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = ir(s);
  if (r?.floes) return floesHold(s, e, px);
  if (r?.seal) return sealHold(s, e, px);
  return false;
};
